import { useEffect, useState } from "react";
import { Lock, Phone, Mail } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useMemberAuth } from "../../Database/RekebishaMembers/MemberAuthContext";
import {
  recordProfileView,
  recordContactView,
  recordPropertyProfileView,
  recordPropertyContactView,
  CONTACT_CHANNELS,
} from "../../Database/RekebishaMembers/analytics";
import { callLink, whatsappLink, emailLink } from "../../Database/RekebishaMembers/contactLinks";
import GoogleSignInButton from "../GoogleSignInButton";

/**
 * Hides a handyman's, property's, or agency's phone/email behind Google sign-in,
 * the same way the app's HandymanDetail/AgencyDetail/PropertyCard never render a
 * contact number as text — only Call/WhatsApp/Email buttons that carry it in their href.
 *
 * subjectType/subjectId key the (shared, app-synced) handymanStats/propertyStats
 * analytics — "agency" doesn't carry an equivalent counter upstream, so an
 * agency-level contact card (as opposed to a per-property one) simply skips the write.
 */
const ContactGate = ({ subjectType, subjectId, name = "this listing", phone, email, size = "default" }) => {
  const { user, signIn, isSigningIn, signOut } = useMemberAuth();
  const [error, setError] = useState(null);
  const [hasCountedView, setHasCountedView] = useState(false);

  const isRevealed = !!user;
  const compact = size === "compact";

  useEffect(() => {
    if (!isRevealed || !subjectId || hasCountedView) return;

    if (subjectType === "handyman") {
      setHasCountedView(true);
      recordProfileView(subjectId);
    } else if (subjectType === "property") {
      setHasCountedView(true);
      recordPropertyProfileView(subjectId);
    }
  }, [isRevealed, subjectType, subjectId, hasCountedView]);

  const handleSignIn = async () => {
    setError(null);
    const { error: signInError } = await signIn();
    if (signInError) setError(signInError);
  };

  const onContact = (channel) => {
    if (subjectType === "handyman") {
      recordContactView(subjectId, channel);
    } else if (subjectType === "property") {
      recordPropertyContactView(subjectId, channel);
    }
    if (typeof window.fbq === "function") {
      window.fbq("track", "Lead", { content_type: subjectType, content_ids: [subjectId], channel });
    }
  };

  const hasPhone = !!phone;
  const hasEmail = !!email;
  const firstName = (name || "").split(" ")[0] || "them";

  if (!isRevealed) {
    return (
      <div className={compact ? "" : "rounded-xl border border-gray-100 bg-gray-50 p-4"}>
        {!compact && (
          <div className="flex items-start gap-2 mb-3">
            <Lock className="w-4 h-4 text-brandgreen flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-navy">Contact details are protected</p>
              <p className="text-xs text-gray-600 mt-0.5">
                Sign in with Google to reach {firstName} directly.
              </p>
            </div>
          </div>
        )}

        {compact ? (
          <button
            type="button"
            onClick={handleSignIn}
            disabled={isSigningIn}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-60"
          >
            <Lock className="w-3.5 h-3.5" />
            {isSigningIn ? "Signing in…" : "View Contact Details"}
          </button>
        ) : (
          <GoogleSignInButton
            onClick={handleSignIn}
            loading={isSigningIn}
            label="Sign in to view contact details"
          />
        )}

        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>
    );
  }

  if (!hasPhone && !hasEmail) {
    return (
      <p className="text-sm text-gray-500">
        {name} {name.endsWith("s") ? "have" : "has"} not published contact details yet.
      </p>
    );
  }

  return (
    <div>
      <div className={`flex gap-2 ${compact ? "flex-wrap" : ""}`}>
        {hasPhone && (
          <a
            href={callLink(phone)}
            onClick={() => onContact(CONTACT_CHANNELS.CALL)}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 bg-navy text-white rounded-lg hover:bg-navy-dark transition-colors font-medium ${
              compact ? "py-2 px-3 text-xs" : "py-3 px-4 text-sm"
            }`}
          >
            <Phone className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
            Call
          </a>
        )}

        {hasPhone && (
          <a
            href={whatsappLink(phone, `Hi, I found ${name} on Rekebisha and I'd like to know more.`)}
            target="_blank"
            rel="noreferrer"
            onClick={() => onContact(CONTACT_CHANNELS.WHATSAPP)}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium ${
              compact ? "py-2 px-3 text-xs" : "py-3 px-4 text-sm"
            }`}
          >
            <FaWhatsapp className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
            WhatsApp
          </a>
        )}

        {hasEmail && (
          <a
            href={emailLink(email, `About ${name} on Rekebisha`)}
            onClick={() => onContact(CONTACT_CHANNELS.EMAIL)}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 bg-brandgreen text-white rounded-lg hover:bg-brandgreen-dark transition-colors font-medium ${
              compact ? "py-2 px-3 text-xs" : "py-3 px-4 text-sm"
            }`}
          >
            <Mail className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
            Email
          </a>
        )}
      </div>

      {!compact && (
        <p className="text-xs text-gray-400 mt-2">
          Signed in as {user.email} ·{" "}
          <button type="button" onClick={signOut} className="underline hover:text-gray-600">
            Sign out
          </button>
        </p>
      )}
    </div>
  );
};

export default ContactGate;
